import React, {FC, useState} from 'react';
import styled from 'styled-components';
import {
  Flyout,
  Icon,
  GenericIconName,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  useModalPortalElement,
} from '@aboutyou/ui-components';
import {useHtmlDir, useTranslation} from '@aboutyou/i18n';
import {styled as linariaStyled} from '@linaria/react';
import {SelectOptionsProps} from './LanguageCountrySwitch';
import {CountriesList, CountriesOpenerContent, Languages} from './shared';
import {createPortal} from 'react-dom';

export const LanguageCountrySwitchFlyout: FC<
  SelectOptionsProps & {
    onClose: () => void;
    open: boolean;
    selectedCountryFlagSrc: string;
    selectedCountryLabel: string;
  }
> = ({
  countries,
  languages,
  onClose,
  open,
  selectedCountryFlagSrc,
  selectedCountryLabel,
}) => {
  const {t} = useTranslation();

  const [showCountriesList, setShowCountriesList] = useState(false);
  const isRtlHtmlDir = useHtmlDir() === 'rtl';

  const hasLanguagesToSelect = Boolean(languages?.length);
  const modalPortalElement = useModalPortalElement();

  if (!modalPortalElement) {
    return null;
  }

  return createPortal(
    <>
      <Flyout
        headerContent={
          <Title>
            {hasLanguagesToSelect ? t('Language and Country') : t('Country')}
          </Title>
        }
        onClose={onClose}
        open={open}
        showHeaderDivider={false}
        bodyContent={
          <>
            {hasLanguagesToSelect && (
              <LanguagesWrapper>
                <Languages languages={languages!} />
              </LanguagesWrapper>
            )}
            <CountriesOpener
              data-testid="languageCountrySwitchCountriesOpener"
              onClick={() => setShowCountriesList(true)}
            >
              <CountriesOpenerContent
                countryFlagSrc={selectedCountryFlagSrc}
                countryLabel={selectedCountryLabel}
                Icon={
                  <ChevronIcon
                    asset={
                      isRtlHtmlDir
                        ? GenericIconName.ArrowLeft
                        : GenericIconName.ArrowRight
                    }
                  />
                }
              />
            </CountriesOpener>
          </>
        }
        dataTestId="languageCountrySwitchFlyout"
      />
      <Flyout
        bodyContent={
          <CountriesListWrapper>
            <CountriesList countries={countries} />
          </CountriesListWrapper>
        }
        dataTestId="languageCountrySwitchFlyoutCountries"
        headerContent={<Title>{t('Select country')}</Title>}
        onClose={() => setShowCountriesList(false)}
        open={showCountriesList}
      />
    </>,
    modalPortalElement,
  );
};

const OUTER_PADDING = '1rem';

const Title = styled.span`
  color: ${COLORS.Black};
  font-size: ${FONT_SIZES.px18};
  font-weight: ${FONT_WEIGHTS.bold};
  line-height: 1;
  margin-bottom: 2px;
  padding: 0.5rem 0 0.375rem;
  text-align: center;
`;

const LanguagesWrapper = styled.div`
  border-bottom: 1px solid ${COLORS.Grey3};
  padding: 0.5rem ${OUTER_PADDING} 1.5rem;
`;

const CountriesOpener = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  padding: 1.5rem ${OUTER_PADDING};
`;

const ChevronIcon = linariaStyled(Icon)`
  fill: ${COLORS.Grey1};
  height: 1rem;
  margin-left: 0.75rem;
  width: 1rem;
`;

const CountriesListWrapper = styled.div`
  overflow-y: auto;
`;
